import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const BudgetAlertSettings = ({ preferences, updatePreferences }) => {
  const budgetAlerts = preferences?.budgetAlerts || {
    enabled: false,
    spendingLimit: 1000,
    emailEnabled: true,
    smsEnabled: false,
    thresholds: [80, 90, 100],
  };

  const handleToggle = (field) => {
    updatePreferences('budgetAlerts', {
      ...budgetAlerts,
      [field]: !budgetAlerts?.[field],
    });
  };

  const handleSpendingLimitChange = (value) => {
    const numValue = parseFloat(value) || 0;
    updatePreferences('budgetAlerts', {
      ...budgetAlerts,
      spendingLimit: numValue,
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
          <Icon name="AlertTriangle" size={24} className="text-orange-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-1 font-heading">
            Budget Alerts
          </h3>
          <p className="text-sm text-muted-foreground">
            Get notified when approaching your monthly spending limit
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Enable Budget Alerts */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Enable Budget Alerts</p>
            <p className="text-xs text-muted-foreground mt-1">
              Receive notifications when reaching spending thresholds
            </p>
          </div>
          <Checkbox
            checked={budgetAlerts?.enabled}
            onChange={() => handleToggle('enabled')}
          />
        </div>

        {budgetAlerts?.enabled && (
          <>
            {/* Spending Limit */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Monthly Spending Limit
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={budgetAlerts?.spendingLimit}
                  onChange={(e) => handleSpendingLimitChange(e?.target?.value)}
                  className="pl-8"
                  placeholder="1000"
                  min="0"
                  step="10"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Set your maximum monthly spending threshold
              </p>
            </div>

            {/* Alert Thresholds */}
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">
                Alert Thresholds
              </label>
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-sm text-foreground">80% Warning</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    ${(budgetAlerts?.spendingLimit * 0.8)?.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-sm text-foreground">90% Urgent</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    ${(budgetAlerts?.spendingLimit * 0.9)?.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-sm text-foreground">100% Critical</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    ${budgetAlerts?.spendingLimit?.toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                You'll receive alerts at each threshold level
              </p>
            </div>

            {/* Notification Channels */}
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">
                Notification Channels
              </label>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Mail" size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Email Notifications</p>
                      <p className="text-xs text-muted-foreground">Receive alerts via email</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={budgetAlerts?.emailEnabled}
                    onChange={() => handleToggle('emailEnabled')}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="MessageSquare" size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">SMS Notifications</p>
                      <p className="text-xs text-muted-foreground">Receive alerts via text message</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={budgetAlerts?.smsEnabled}
                    onChange={() => handleToggle('smsEnabled')}
                  />
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-1">How Budget Alerts Work</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Alerts are checked daily based on your current month's spending</li>
                    <li>• You'll receive one notification per threshold level per month</li>
                    <li>• View detailed spending breakdown in your dashboard</li>
                    <li>• Adjust your limit anytime in notification preferences</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BudgetAlertSettings;